<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import Modal from "../Modal.vue";
import Button from "../Button.vue";
import Select from "../Select.vue";
import DateField from "../DateField.vue";
import TimeField from "../TimeField.vue";
import SearchField from "../SearchField.vue";
import Avatar from "../Avatar.vue";
import Icon from "../Icon.vue";
import Switch from "../Switch.vue";
import SlotGrid from "./SlotGrid.vue";
import {
  schedulingApi,
  type AgendaDetail,
  type Appointment,
  type IntakeField,
  type Slot,
  type TypeChoice,
} from "../../api/scheduling";
import { contactsListApi, type ContactListRow } from "../../api/organization";
import { contactsApi } from "../../api/contacts";
import { ApiError } from "../../api/client";
import { toast } from "../../composables/feedback";
import { profileTone } from "../../composables/profileColors";
import { dayOf, instantOf, todayIn } from "../../composables/agendaTime";
import {
  dayMonth,
  longWeekday,
  placeText,
  providerLabel,
  schedulingError,
  timeText,
  whenText,
} from "../../composables/agenda";
import { t } from "../../i18n";

/**
 * "Agendar com Leo" (decision 0022, board "Atendente"): the type, who attends, the type's
 * questions and the free times; "Ver mais dias ou escolher outro horário" pages the days and
 * lets the team book outside the free times (still refused when the person is taken). Without a
 * contact it first asks for whom; with an appointment it moves it (Remarcar).
 */
const props = withDefaults(
  defineProps<{
    open: boolean;
    slug: string;
    contact?: { id: string; name: string } | null;
    conversationId?: string | null;
    /** Reschedule this one. */
    appointment?: Appointment | null;
    /** The conversation's 24-hour window: how the confirmation goes. Unknown outside a conversation. */
    windowOpen?: boolean | null;
  }>(),
  {
    contact: null,
    conversationId: null,
    appointment: null,
    windowOpen: null,
  },
);
const emit = defineEmits<{ close: []; booked: [appointment: Appointment] }>();
const router = useRouter();

const LIMIT = 500;
const rescheduling = computed(() => Boolean(props.appointment));
const picked = ref<{ id: string; name: string } | null>(null);
const who = computed(() =>
  props.appointment
    ? { id: props.appointment.contactId, name: props.appointment.contactName }
    : (props.contact ?? picked.value),
);

// ---- for whom (only when opened without a contact)
const query = ref("");
const found = ref<ContactListRow[]>([]);
const searching = ref(false);
let searchTimer: ReturnType<typeof setTimeout> | undefined;
async function search() {
  searching.value = true;
  try {
    found.value = (
      await contactsListApi.list(props.slug, {
        ...(query.value.trim() ? { q: query.value.trim() } : {}),
        limit: 30,
      })
    ).contacts;
  } catch (err) {
    toast(schedulingError(err));
  } finally {
    searching.value = false;
  }
}
watch(query, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => void search(), 250);
});

// ---- the type and its agenda
const types = ref<TypeChoice[]>([]);
const typesLoaded = ref(false);
const typeId = ref("");
const type = computed(
  () => types.value.find((x) => x.id === typeId.value) ?? null,
);
/** "Visitas · Visita ao imóvel" when there are several agendas; the type alone otherwise. */
const typeLabel = (x: TypeChoice) =>
  new Set(types.value.map((y) => y.agendaId)).size > 1
    ? `${x.agendaName} · ${x.name}`
    : x.name;
const typeOptions = computed(() =>
  types.value
    .filter(
      (x) =>
        (x.isActive && x.agendaActive) || x.id === props.appointment?.typeId,
    )
    .map(typeLabel),
);
const typeModel = computed({
  get: () => (type.value ? typeLabel(type.value) : ""),
  set: (v: string) => {
    typeId.value = types.value.find((x) => typeLabel(x) === v)?.id ?? "";
  },
});
const timeZone = computed(() => type.value?.timezone ?? "America/Sao_Paulo");
const today = computed(() => todayIn(timeZone.value));

const agenda = ref<AgendaDetail | null>(null);
watch(
  () => type.value?.agendaId,
  async (id) => {
    agenda.value = null;
    if (!id) return;
    agenda.value = await schedulingApi.agenda(props.slug, id).catch(() => null);
  },
);

// ---- who attends
const assignment = computed(() => type.value?.assignment.mode ?? "round_robin");
const isResource = computed(() => agenda.value?.agenda.kind === "resource");
const people = computed(() =>
  (agenda.value?.agenda.members ?? []).filter((m) => m.isActive),
);
const nameOf = (id: string | null | undefined) =>
  agenda.value?.agenda.members.find((m) => m.memberId === id)?.name ?? "";
const memberId = ref<string | null>(null);
const memberLocked = computed(
  () => assignment.value === "fixed" || assignment.value === "collective",
);
const anyone = computed(() => t("book.anyone"));
const memberModel = computed({
  get: () =>
    assignment.value === "collective"
      ? t("book.together")
      : assignment.value === "fixed"
        ? nameOf(type.value?.assignment.memberId)
        : memberId.value
          ? nameOf(memberId.value)
          : anyone.value,
  set: (v: string) => {
    memberId.value = people.value.find((p) => p.name === v)?.memberId ?? null;
  },
});
const memberOptions = computed(() =>
  memberLocked.value
    ? [memberModel.value]
    : [anyone.value, ...people.value.map((p) => p.name)],
);

// ---- the questions, with what the contact already has
const answers = ref<Record<string, string>>({});
const contactFields = ref<Record<string, unknown>>({});
watch(
  () => who.value?.id,
  async (id) => {
    contactFields.value = {};
    if (!id) return;
    const d = await contactsApi.get(props.slug, id).catch(() => null);
    contactFields.value = d?.contact.fields ?? {};
    prefill();
  },
);
function prefill() {
  for (const f of type.value?.intake ?? []) {
    const v = f.saveToField ? contactFields.value[f.saveToField] : undefined;
    if (!answers.value[f.key] && v !== undefined && v !== null && v !== "")
      answers.value[f.key] = String(v);
  }
}
const fieldLabel = (f: IntakeField) =>
  f.required ? t("book.required", { label: f.label }) : f.label;

// ---- free times
const slots = ref<Slot[]>([]);
const loadingSlots = ref(false);
const hasMore = ref(false);
const selected = ref<string | null>(null);
async function loadSlots(append = false) {
  const ty = type.value;
  if (!ty) {
    slots.value = [];
    return;
  }
  const last = slots.value.at(-1);
  loadingSlots.value = true;
  try {
    const r = await schedulingApi.slots(props.slug, ty.id, {
      limit: LIMIT,
      ...(append && last
        ? {
            from: new Date(
              new Date(last.start).getTime() + 60_000,
            ).toISOString(),
          }
        : {}),
      ...(who.value ? { contactId: who.value.id } : {}),
      ...(memberId.value && !memberLocked.value
        ? { memberId: memberId.value }
        : {}),
      ...(props.appointment ? { exclude: props.appointment.id } : {}),
    });
    if (ty.id !== typeId.value) return;
    slots.value = append ? [...slots.value, ...r.slots] : r.slots;
    hasMore.value = r.slots.length === LIMIT;
  } catch (err) {
    toast(schedulingError(err));
  } finally {
    loadingSlots.value = false;
  }
}
watch([typeId, memberId, () => who.value?.id], () => {
  selected.value = null;
  alternatives.value = [];
  existingId.value = null;
  prefill();
  void loadSlots();
});

// ---- another time, outside the free ones
const more = ref(false);
const otherDay = ref("");
const otherTime = ref("");
const forced = computed(() =>
  otherDay.value && /^\d{2}:\d{2}$/.test(otherTime.value)
    ? instantOf(otherDay.value, otherTime.value, timeZone.value).toISOString()
    : null,
);
watch(selected, (v) => {
  if (v) {
    otherDay.value = "";
    otherTime.value = "";
  }
});
watch(forced, (v) => {
  if (v) selected.value = null;
});
const start = computed(() => selected.value ?? forced.value);

// ---- what the API refused
const alternatives = ref<Slot[]>([]);
const existingId = ref<string | null>(null);

// ---- the summary and the WhatsApp note
const summary = computed(() => {
  const s = start.value;
  const ty = type.value;
  if (!s || !ty) return null;
  const tz = timeZone.value;
  const end = new Date(new Date(s).getTime() + ty.durationMin * 60_000);
  const day = dayOf(s, tz);
  const when = t("book.summary_when", {
    day: longWeekday(day),
    date: dayMonth(day),
    start: timeText(s, tz),
    end: timeText(end, tz),
  });
  const slot = slots.value.find((x) => x.start === s);
  const holder =
    assignment.value === "fixed"
      ? (type.value?.assignment.memberId ?? null)
      : memberId.value && !memberLocked.value
        ? memberId.value
        : slot?.memberIds.length === 1
          ? slot.memberIds[0]!
          : null;
  const person = isResource.value
    ? (agenda.value?.agenda.name ?? "")
    : assignment.value === "collective"
      ? t("book.together")
      : holder
        ? nameOf(holder)
        : t("book.summary_anyone");
  const answersAsFields = Object.fromEntries(
    ty.intake
      .filter((f) => f.saveToField && answers.value[f.key]?.trim())
      .map((f) => [f.saveToField!, answers.value[f.key]!.trim()]),
  );
  const where = placeText(ty.location, {
    ...contactFields.value,
    ...answersAsFields,
  });
  const calendar = holder
    ? agenda.value?.calendars.find(
        (c) =>
          c.memberId === holder &&
          c.status === "active" &&
          c.provider !== "ics",
      )
    : undefined;
  const tail = calendar
    ? t("book.summary_calendar", {
        calendar: providerLabel(calendar.provider),
        name: nameOf(holder),
      })
    : holder || isResource.value
      ? t("book.summary_donetalk")
      : t("book.summary_reminders");
  return { when, rest: [person, where].filter(Boolean).join(" · "), tail };
});
const note = computed(() => {
  const notices = agenda.value?.notices ?? [];
  const kind = rescheduling.value ? "rescheduled" : "confirmation";
  if (notices.length && !notices.some((n) => n.kind === kind && n.enabled))
    return t("book.note_off");
  if (rescheduling.value) return t("book.note_rescheduled");
  const freeForm = agenda.value?.agenda.settings.freeFormInWindow ?? true;
  if (props.windowOpen === true && freeForm) return t("book.note_window_open");
  if (props.windowOpen === false || !freeForm) return t("book.note_template");
  return t("book.note_unknown");
});

/** The board's "Mandar a confirmação no WhatsApp agora": on by default, off holds it. */
const sendConfirmation = ref(true);
const confirmationOn = computed(() => {
  const notices = agenda.value?.notices ?? [];
  return (
    !rescheduling.value &&
    (!notices.length ||
      notices.some((n) => n.kind === "confirmation" && n.enabled))
  );
});

// ---- book or move
const busy = ref(false);
async function submit() {
  const s = start.value;
  const ty = type.value;
  const contact = who.value;
  if (!s || !ty || !contact) return;
  busy.value = true;
  alternatives.value = [];
  existingId.value = null;
  const force = !selected.value;
  const member = memberLocked.value ? null : memberId.value;
  try {
    const r = props.appointment
      ? await schedulingApi.reschedule(props.slug, props.appointment.id, {
          startsAt: s,
          memberId: member,
          ...(force ? { force: true } : {}),
        })
      : await schedulingApi.book(props.slug, {
          typeId: ty.id,
          contactId: contact.id,
          startsAt: s,
          conversationId: props.conversationId,
          memberId: member,
          answers: Object.fromEntries(
            Object.entries(answers.value)
              .map(([k, v]) => [k, v.trim()])
              .filter(([, v]) => v),
          ),
          ...(force ? { force: true } : {}),
          ...(sendConfirmation.value ? {} : { confirm: false }),
        });
    toast(
      t(props.appointment ? "book.done_rescheduled" : "book.done", {
        when: whenText(r.appointment),
      }),
    );
    emit("booked", r.appointment);
  } catch (err) {
    if (err instanceof ApiError && err.code === "slot_taken") {
      const d = err.details as {
        alternatives?: Array<{ start: string; end: string }>;
      };
      alternatives.value = (d?.alternatives ?? []).map((a) => ({
        ...a,
        memberIds: [],
      }));
      selected.value = null;
      void loadSlots();
    } else if (
      err instanceof ApiError &&
      err.code === "contact_has_appointment"
    ) {
      const d = err.details as { appointmentIds?: string[] };
      existingId.value = d?.appointmentIds?.[0] ?? null;
    } else toast(schedulingError(err));
  } finally {
    busy.value = false;
  }
}
function openExisting() {
  const id = existingId.value;
  if (!id) return;
  emit("close");
  void router.push({ path: "/agenda", query: { appointment: id } });
}

// ---- opening resets everything
watch(
  () => props.open,
  async (open) => {
    if (!open) return;
    picked.value = null;
    query.value = "";
    found.value = [];
    answers.value = {};
    selected.value = null;
    more.value = false;
    otherDay.value = "";
    otherTime.value = "";
    alternatives.value = [];
    existingId.value = null;
    memberId.value = props.appointment?.memberId ?? null;
    typesLoaded.value = false;
    if (!who.value) void search();
    const list = await schedulingApi
      .types(props.slug)
      .then((r) => r.types)
      .catch((err) => {
        toast(schedulingError(err));
        return [] as TypeChoice[];
      });
    types.value = list;
    typesLoaded.value = true;
    const usable = list.filter((x) => x.isActive && x.agendaActive);
    const next = props.appointment?.typeId ?? usable[0]?.id ?? "";
    if (next === typeId.value) void loadSlots();
    else typeId.value = next;
  },
  { immediate: true },
);

/** Three days a time on a phone, five on a wider screen. */
const narrow = ref(false);
let media: MediaQueryList | null = null;
const onMedia = () => (narrow.value = Boolean(media?.matches));
onMounted(() => {
  media = window.matchMedia("(max-width: 560px)");
  onMedia();
  media.addEventListener("change", onMedia);
});
onBeforeUnmount(() => {
  media?.removeEventListener("change", onMedia);
  clearTimeout(searchTimer);
});

const title = computed(() =>
  props.appointment
    ? t("book.title_reschedule", { name: who.value?.name ?? "" })
    : who.value
      ? t("book.title", { name: who.value.name })
      : t("book.title_pick"),
);
</script>
<template>
  <Modal :open="open" :title="title" wide @close="emit('close')">
    <div v-if="!who" class="stack book-pick">
      <p class="small muted">{{ t("book.pick_hint") }}</p>
      <SearchField v-model="query" :placeholder="t('book.pick_search')" />
      <div class="contact-share-list scroll">
        <button
          v-for="c in found"
          :key="c.id"
          type="button"
          class="list-row contact-share-row"
          @click="picked = { id: c.id, name: c.displayName }"
        >
          <Avatar
            :name="c.displayName"
            :tone="profileTone(c.address, c.id)"
            :size="36"
          />
          <span class="grow">
            <strong>{{ c.displayName }}</strong>
            <small v-if="c.address" class="muted">+{{ c.address }}</small>
          </span>
        </button>
      </div>
      <p v-if="!found.length && !searching" class="muted small">
        {{ t("book.pick_empty") }}
      </p>
    </div>

    <div v-else class="book-form">
      <p v-if="picked && !contact && !appointment" class="book-for small">
        <span class="muted">{{ t("book.for") }}</span>
        <strong>{{ picked.name }}</strong>
        <button type="button" class="link-button small" @click="picked = null">
          {{ t("book.change_contact") }}
        </button>
      </p>
      <p v-if="appointment" class="book-for small">
        <span class="muted">{{ t("book.from_when") }}</span>
        <strong>{{ whenText(appointment) }}</strong>
      </p>
      <p v-if="typesLoaded && !typeOptions.length" class="muted">
        {{ t("book.no_types") }}
      </p>
      <div class="book-fields">
        <Select
          v-model="typeModel"
          :label="t('book.type')"
          :options="typeOptions"
          :disabled="rescheduling || !typeOptions.length"
        />
        <Select
          v-if="type && !isResource"
          v-model="memberModel"
          :label="t('book.who')"
          :options="memberOptions"
          :disabled="memberLocked"
        />
        <template v-if="!rescheduling">
          <template v-for="f in type?.intake ?? []" :key="f.key">
            <Select
              v-if="f.kind === 'choice'"
              :model-value="answers[f.key] ?? ''"
              :label="fieldLabel(f)"
              :options="f.options"
              @update:model-value="(v) => (answers[f.key] = v)"
            />
            <label v-else class="wa-field"
              ><span>{{ fieldLabel(f) }}</span
              ><input v-model="answers[f.key]" maxlength="500" placeholder=" "
            /></label>
          </template>
        </template>
      </div>

      <SlotGrid
        v-if="type"
        v-model="selected"
        :slots="slots"
        :time-zone="timeZone"
        :per-page="narrow ? 3 : 5"
        :per-day="4"
        :paged="more"
        :has-more="hasMore"
        :loading="loadingSlots"
        @more="loadSlots(true)"
      />
      <button
        v-if="type && !more"
        type="button"
        class="link-button"
        @click="more = true"
      >
        {{ t("book.more") }}
      </button>
      <div v-if="type && more" class="book-other">
        <strong>{{ t("book.other_title") }}</strong>
        <p class="small muted">{{ t("book.other_hint") }}</p>
        <div class="book-other-fields">
          <DateField
            v-model="otherDay"
            :label="t('book.other_day')"
            :min="today"
          />
          <TimeField v-model="otherTime" :label="t('book.other_time')" field />
        </div>
      </div>

      <div v-if="alternatives.length" class="book-alert" role="alert">
        <p>{{ t("book.taken") }}</p>
        <div class="row sg-wrap">
          <button
            v-for="a in alternatives"
            :key="a.start"
            type="button"
            class="slot-chip"
            :class="{ on: selected === a.start }"
            @click="selected = a.start"
          >
            {{ longWeekday(dayOf(a.start, timeZone)) }}
            {{ dayMonth(dayOf(a.start, timeZone)) }} ·
            {{ timeText(a.start, timeZone) }}
          </button>
        </div>
      </div>
      <div v-if="existingId" class="book-alert" role="alert">
        <p>{{ t("book.has_one", { name: who.name }) }}</p>
        <Button
          variant="secondary"
          compact
          icon="calendar"
          @click="openExisting"
          >{{ t("book.open_existing") }}</Button
        >
      </div>

      <Switch
        v-if="type && confirmationOn"
        v-model="sendConfirmation"
        class="switch-line"
        :label="t('book.send_confirmation')"
        :hint="sendConfirmation ? note : t('book.confirmation_held')"
      />
      <p v-else-if="type" class="book-note small">
        <Icon name="chat" :size="18" /><span>{{ note }}</span>
      </p>
      <p v-if="summary" class="book-summary">
        <b>{{ summary.when }}</b
        ><template v-if="summary.rest"> · {{ summary.rest }}</template
        >.
        {{ summary.tail }}
      </p>
    </div>

    <template #footer>
      <Button variant="secondary" @click="emit('close')">{{
        t("common.cancel")
      }}</Button>
      <Button
        v-if="who"
        :loading="busy"
        :disabled="!start || !type"
        @click="submit"
        >{{
          appointment ? t("book.submit_reschedule") : t("book.submit")
        }}</Button
      >
    </template>
  </Modal>
</template>
